import { Stack, styled } from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import Typography from '@mui/material/Typography';
import { TFile } from 'types/user';
import { createInboxChat, hiddenInboxChat, hiddenInboxChatByFileId } from 'services/files';
import { FileInboxItem } from './FileInboxItem';
import { useAsyncCallback } from 'hooks/useAsyncCallback';
import { useFlash } from 'contexts/flash';
import { FileInboxNotesTableCb } from './FileInboxNotesTable';
import { useDimension } from 'hooks/useDimension';
import { markNotesAsRead, subscribeInbox } from 'services/communication';
import dayjs from 'dayjs';
import { StorageServices } from 'services/storage';
import { ROLE } from 'const/common';
import { useNewMessage } from 'contexts/newMessage';

const storage = new StorageServices();
type ChatBoxTabProps = {
  isMobile?: boolean;
};
export const ChatBoxTab = ({ isMobile }: ChatBoxTabProps) => {
  const { setFlash } = useFlash();
  const { refreshStatus } = useNewMessage();
  const [files, setFiles] = useState<TFile[]>([]);
  const [selectedFile, setSelectedFile] = useState<TFile | null>(null);
  const { height } = useDimension();
  const inboxRef = React.useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!selectedFile) return;
    const stillExist = files.find((f) => f.id === selectedFile.id);
    if (stillExist) {
      setSelectedFile(stillExist);
    }
  }, [files]);

  const handleSenMessage = useCallback(
    async (content: string, fileId: string) => {
      const dataRes = await createInboxChat(content, Number(fileId));
      setFiles((pre) =>
        pre.map((item) => {
          if (Number(item.id) === Number(fileId)) {
            return {
              ...item,
              communicationNotes: [dataRes.note, ...item.communicationNotes]
            };
          }
          return item;
        })
      );
      setFlash({ type: 'success', message: 'Created successful' });
    },
    [setFlash]
  );

  const handleDeleteAllMessage = useCallback(
    async (fileId: number) => {
      if (!fileId) return;
      const confirmDelete = window.confirm('Are you sure you want to delete this message?');
      if (!confirmDelete) return;
      await hiddenInboxChatByFileId(fileId);
      setSelectedFile(null);
      setFiles((pre) => pre.filter((item) => Number(item.id) !== fileId));
      setFlash({ type: 'success', message: 'Deleted successful' });
    },
    [setFlash]
  );

  const handleDeleteMessageDetail = useCallback(
    async (fileId: number, chatId: number) => {
      if (!chatId) return;
      const confirmDelete = window.confirm('Are you sure you want to delete this message?');
      if (!confirmDelete) return;
      await hiddenInboxChat(chatId);
      setFiles((pre) =>
        pre.map((item) => {
          if (Number(item.id) === fileId) {
            return {
              ...item,
              communicationNotes: item.communicationNotes.filter(
                (itemNote) => itemNote.id !== chatId
              )
            };
          }
          return item;
        })
      );
      setFlash({ type: 'success', message: 'Deleted successful' });
    },
    [setFlash]
  );

  useEffect(() => {
    const unsubscribe = subscribeInbox((data) => {
      setFiles(data.files);
    });
    if (!unsubscribe) return;
    return () => {
      unsubscribe();
    };
  }, []);

  const { asyncCallback: asyncHandleSenMessage } = useAsyncCallback(handleSenMessage, []);
  const { asyncCallback: asyncHandleDeleteAllMessage } = useAsyncCallback(
    handleDeleteAllMessage,
    []
  );
  const { asyncCallback: asyncHandleDeleteMessageDetail } = useAsyncCallback(
    handleDeleteMessageDetail,
    []
  );

  return (
    <Stack>
      <WrapperBox
        sx={{
          borderWidth: { xs: 0, md: '1px' },
          marginTop: { xs: 0, sm: '20px' }
        }}>
        <Stack
          p={{ xs: 1, sm: 2 }}
          flexDirection={'row'}
          alignItems={'start'}
          sx={{ borderBottom: { xs: 'none', sm: '1px solid #686E7D' } }}
          justifyContent={'space-between'}>
          <Typography fontSize={18} fontWeight={700} marginBottom={2.5}>
            Inbox
          </Typography>
        </Stack>
        <Stack direction="row" p={{ xs: 0.5, sm: 2 }} gap={2} height="100%">
          {/* LEFT: LIST (1/3) */}
          <Stack
            width={{ xs: '100%', md: '33%' }}
            height={{ xs: height - 190, md: height - 400 }}
            id="inboxScroll"
            ref={inboxRef}
            sx={{ overflowY: 'auto' }}>
            <Stack gap={1.25}>
              {files.map((file) => (
                <FileInboxItem
                  key={file.id}
                  isMobile={!!isMobile}
                  file={file}
                  onDeleteDetail={asyncHandleDeleteMessageDetail}
                  onSendMessage={asyncHandleSenMessage}
                  onDeleteByFileId={asyncHandleDeleteAllMessage}
                  onClick={async ({
                    isUnread,
                    onReply
                  }: {
                    isUnread?: boolean;
                    onReply?: boolean;
                  }) => {
                    if (selectedFile?.id === file.id && !onReply) {
                      setSelectedFile(null);
                    } else {
                      setSelectedFile(file);
                      if (isUnread) {
                        const role = storage.getRole();
                        const isAdmin = role === ROLE.ADMIN || role === ROLE.ROOT_ADMIN;
                        setFiles((prevState) =>
                          prevState.map((item) => {
                            if (item.id === file.id) {
                              return {
                                ...item,
                                communicationNotes: item.communicationNotes.map((itemInbox) => ({
                                  ...itemInbox,
                                  isAdminRead: isAdmin ? true : itemInbox.isAdminRead,
                                  communicationNoteReads: !isAdmin
                                    ? itemInbox.communicationNoteReads.map((itemRead) => ({
                                        ...itemRead,
                                        readAt: dayjs().toString()
                                      }))
                                    : itemInbox.communicationNoteReads
                                }))
                              };
                            }
                            return item;
                          })
                        );
                        await markNotesAsRead(Number(file.id));
                        refreshStatus();
                      }
                    }
                  }}
                  selectedFile={selectedFile}
                  selected={selectedFile?.id === file.id}
                />
              ))}
            </Stack>
          </Stack>

          {/* RIGHT: DETAIL (2/3) */}
          <Stack
            width="67%"
            sx={{ overflowY: 'auto', display: { xs: 'none', md: 'block' } }}
            height={height - 400}
            border="1px solid #E0E0E0"
            borderRadius={2}
            p={2}>
            {selectedFile ? (
              <FileInboxNotesTableCb
                notes={selectedFile.communicationNotes}
                onDeleteDetail={(idChat) =>
                  asyncHandleDeleteMessageDetail(Number(selectedFile.id), idChat)
                }
              />
            ) : (
              <Typography color="text.secondary">Select an inbox to view details</Typography>
            )}
          </Stack>
        </Stack>
      </WrapperBox>
    </Stack>
  );
};

const WrapperBox = styled(Stack)(() => ({
  border: '1px solid #686E7D',
  padding: '14px 0 0 0',
  borderRadius: '16px',
  flexDirection: 'column'
}));
// const NewMessageText = styled(Typography)(({ theme }) => ({
//   fontWeight: 600,
//   position: 'relative',
//   color: theme.palette.primary.main,
//   animation: 'pulse 1.8s ease-in-out infinite',
//
//   '@keyframes pulse': {
//     '0%': {
//       opacity: 1,
//       transform: 'scale(1)'
//     },
//     '50%': {
//       transform: 'scale(1.05)'
//     },
//     '100%': {
//       opacity: 1,
//       transform: 'scale(1)'
//     }
//   },
//
//   '&::after': {
//     content: '""',
//     position: 'absolute',
//     top: 2,
//     right: -10,
//     width: 8,
//     height: 8,
//     borderRadius: '50%',
//     backgroundColor: theme.palette.error.main
//   }
// }));
